import { RefObject } from 'react';
import { Play, Pause, Volume2, VolumeX, CircleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TimelineComment } from '@/types/video-validation';

interface InlineVideoPlayerProps {
  readonly videoRef: RefObject<HTMLVideoElement>;
  readonly videoUrl: string | null;
  readonly isPlaying: boolean;
  readonly isMuted: boolean;
  readonly currentTime: number;
  readonly duration: number;
  readonly isValidated: boolean;
  readonly comments: TimelineComment[];
  readonly activeMarkerId: string | null;
  readonly onTogglePlay: () => void;
  readonly onToggleMute: () => void;
  readonly onSeek: (time: number) => void;
  readonly onMarkerClick: (comment: TimelineComment) => void;
  readonly onTimeUpdate: () => void;
  readonly onLoadedMetadata: () => void;
  readonly onPlay: () => void;
  readonly onPause: () => void;
  readonly formatTime: (seconds: number) => string;
}

export function InlineVideoPlayer({
  videoRef,
  videoUrl,
  isPlaying,
  isMuted,
  currentTime,
  duration,
  isValidated,
  comments,
  activeMarkerId,
  onTogglePlay,
  onToggleMute,
  onSeek,
  onMarkerClick,
  onTimeUpdate,
  onLoadedMetadata,
  onPlay,
  onPause,
  formatTime,
}: InlineVideoPlayerProps) {
  return (
    <section
      className="relative w-full h-full rounded-3xl overflow-hidden cursor-pointer"
      style={{
        boxShadow: isValidated
          ? '0 0 0 2px hsla(170, 80%, 50%, 0.5)'
          : '0 0 0 2px hsla(49, 100%, 52%, 0.5)',
      }}
      aria-label="Lecteur vidéo principal"
    >
      {/* Status badge */}
      <div className="absolute top-3 right-3 z-10">
        <span
          className={cn(
            'inline-flex items-center rounded-full text-[10px] font-medium uppercase tracking-[0.05em] px-2 py-1 gap-1.5',
            isValidated
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-[rgba(255,207,12,0.1)] border border-[rgba(255,207,12,0.3)] text-warning',
          )}
        >
          <CircleAlert className="w-3 h-3" />
          {isValidated ? 'VALIDÉ' : 'À VALIDER'}
        </span>
      </div>

      {videoUrl ? (
        <button
          type="button"
          className="w-full h-full relative p-0 border-0 bg-transparent cursor-pointer"
          onClick={onTogglePlay}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onTogglePlay();
            }
          }}
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          <video
            ref={videoRef}
            src={`${videoUrl}#t=0.001`}
            className="w-full h-full object-cover"
            muted={isMuted}
            playsInline
            preload="auto"
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={onLoadedMetadata}
            onPlay={onPlay}
            onPause={onPause}
          >
            <track kind="captions" srcLang="fr" label="Français" default />
          </video>

          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
              <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Play className="h-6 w-6 text-white ml-1" />
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleMute();
            }}
            className="absolute top-3 left-3 z-20 bg-black/50 backdrop-blur-sm rounded-full p-2 text-white hover:bg-black/70 transition-all"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>

          {/* Progress bar */}
          <div
            className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/80 to-transparent"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={onTogglePlay}
                className="text-white flex-shrink-0"
                type="button"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4 fill-white" />
                )}
              </button>
              <div className="flex-1 relative">
                {/* Comment markers */}
                {comments.filter((c) => !c.parentId).length > 0 && (
                  <div className="absolute -top-3 left-[6px] right-[6px] h-3">
                    {comments
                      .filter((c) => !c.parentId)
                      .map((comment) => {
                        const position =
                          duration > 0 ? (comment.timestamp / duration) * 100 : 0;
                        return (
                          <button
                            key={comment.id}
                            className="absolute -translate-x-1/2 bottom-0 cursor-pointer p-0 border-none bg-transparent"
                            style={{ left: `${position}%` }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkerClick(comment);
                            }}
                            aria-label={`Commentaire à ${Math.floor(comment.timestamp / 60)}:${Math.floor(comment.timestamp % 60).toString().padStart(2, '0')}`}
                          >
                            <div
                              className={cn(
                                'w-2 h-2 rounded-full shadow-lg border border-black/30',
                                activeMarkerId === comment.id
                                  ? 'bg-green-400'
                                  : comment.isSent
                                    ? 'bg-amber-400'
                                    : 'bg-white/50',
                              )}
                            />
                          </button>
                        );
                      })}
                  </div>
                )}
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => onSeek(Number(e.target.value))}
                  className="w-full h-1 cursor-pointer appearance-none bg-white/20 rounded-full accent-white"
                  aria-label="Video progress"
                />
              </div>
              <span className="text-xs text-white/50 font-mono tabular-nums">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
          </div>
        </button>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-[#1f1f1f] text-white/30 text-sm">
          Aucune vidéo disponible
        </div>
      )}
    </section>
  );
}
